import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { Toaster } from 'react-hot-toast';
import { initPerformanceOptimizations } from './utils/performance';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';

// Styles
import GlobalStyles from './styles/GlobalStyles';
import theme from './styles/theme';
import './styles/ImageTheme.css';

// Components
import HeaderSlim from './components/Header';
import Footer from './components/Footer';
import LoadingSpinner from './components/LoadingSpinner';
import MobileBookBar from './components/MobileBookBar';
import MascotWalker from './components/MascotWalker';
import FloatingCart from './components/FloatingCart';
import ScrollToTop from './components/ScrollToTop';
import RequireAdmin from './components/admin/RequireAdmin';
import ChatbotWidget from './components/chatbot/ChatbotWidget';
import ManageBooking from './pages/ManageBooking';
import Success from './pages/Success';
import MobileBar from './components/nav/MobileBar';
import LocalBusinessJsonLD from './components/seo/LocalBusinessJsonLD';

// Critical pages (loaded immediately)
import Home from './pages/Home';

// Lazy load non-critical pages
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Booking = lazy(() => import('./pages/Booking'));
const Checkout = lazy(() => import('./pages/Checkout'));
const CartCheckout = lazy(() => import('./pages/CartCheckout'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Login = lazy(() => import('./pages/Login'));
const Admin = lazy(() => import('./pages/Admin'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const FAQ = lazy(() => import('./pages/FAQ'));

const BounceHouses = lazy(() => import('./pages/categories/BounceHouses'));
const WaterSlides = lazy(() => import('./pages/categories/WaterSlides'));
const ObstacleCourses = lazy(() => import('./pages/categories/ObstacleCourses'));
const EventRentals = lazy(() => import('./pages/categories/EventRentals'));
const Combos = lazy(() => import('./pages/categories/Combos'));



// Lazy load admin components
const AdminLogin = lazy(() => import('./pages/admin/Login'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminInventory = lazy(() => import('./pages/admin/Inventory'));
const ProductManagement = lazy(() => import('./pages/admin/ProductManagement'));
const AdminCalendar = lazy(() => import('./pages/admin/AdminCalendar'));
const DeliveryManagement = lazy(() => import('./pages/admin/DeliveryManagement'));
const FinancialReports = lazy(() => import('./pages/admin/FinancialReports'));
const CustomerDashboard = lazy(() => import('./pages/CustomerDashboard'));
const MarketingAutomation = lazy(() => import('./pages/admin/MarketingAutomation'));
const AdvancedAnalytics = lazy(() => import('./pages/admin/AdvancedAnalytics'));
const LoyaltyDashboard = lazy(() => import('./components/loyalty/LoyaltyDashboard'));
const Phase4Dashboard = lazy(() => import('./pages/admin/Phase4Dashboard'));
const SecurityDashboard = lazy(() => import('./components/security/SecurityDashboard'));

// Protected Route Component
const ProtectedRoute = ({ children, requireAuth = true, requireAdmin = false }) => {
  const { user, loading, userProfile } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (requireAuth && !user) {
    return <Login />;
  }

  if (requireAdmin && userProfile?.role !== 'admin') {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '50px 20px',
        color: theme.colors.text.primary 
      }}>
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
      </div>
    );
  }

  return children;
};

// Main App Component
const AppContent = () => {
  const { loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Router>
      <ScrollToTop />
      <GlobalStyles />
      <HeaderSlim />
      <MobileBookBar />
      <MascotWalker enable={true} showAfterMs={2000} />
      <FloatingCart />
      <div className="App">
        <main>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
                                {/* Public Routes */}
                    <Route path="/" element={<Home />} />
                    <Route path="/products" element={<Products />} />
                    <Route path="/bounce-houses" element={<BounceHouses />} />
                    <Route path="/water-slides" element={<WaterSlides />} />
                    <Route path="/combos" element={<Combos />} />
                    <Route path="/obstacle-courses" element={<ObstacleCourses />} />
                    <Route path="/event-rentals" element={<EventRentals />} />
                    <Route path="/products/:slug" element={<ProductDetail />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/login" element={<Login />} />

                    <Route path="/booking" element={<Booking />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/cart-checkout" element={<CartCheckout />} />
                    <Route path="/manage/:token" element={<ManageBooking />} />
                    <Route path="/success" element={<Success />} />
                    
                    {/* Protected Routes */}
                    <Route 
                      path="/booking/:productId" 
                      element={
                        <ProtectedRoute requireAuth={true}>
                          <Booking />
                        </ProtectedRoute>
                      } 
                    />
            <Route 
              path="/checkout/:bookingId" 
              element={
                <ProtectedRoute requireAuth={true}>
                  <Checkout />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute requireAuth={true}>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
            <Route path="/admin/inventory" element={<RequireAdmin><AdminInventory /></RequireAdmin>} />
        <Route path="/admin/products" element={<RequireAdmin><ProductManagement /></RequireAdmin>} />
        <Route path="/admin/products/new" element={<RequireAdmin><ProductManagement showAddModal={true} /></RequireAdmin>} />
        <Route path="/admin/calendar" element={<RequireAdmin><AdminCalendar /></RequireAdmin>} />
        <Route path="/admin/deliveries" element={<RequireAdmin><DeliveryManagement /></RequireAdmin>} />
        <Route path="/admin/reports" element={<RequireAdmin><FinancialReports /></RequireAdmin>} />
        <Route path="/customer-dashboard" element={<ProtectedRoute><CustomerDashboard /></ProtectedRoute>} />
        <Route path="/admin/marketing" element={<RequireAdmin><MarketingAutomation /></RequireAdmin>} />
        <Route path="/admin/analytics" element={<RequireAdmin><AdvancedAnalytics /></RequireAdmin>} />
        <Route path="/loyalty" element={<ProtectedRoute><LoyaltyDashboard /></ProtectedRoute>} />
        <Route path="/admin/phase4" element={<RequireAdmin><Phase4Dashboard /></RequireAdmin>} />
        <Route path="/admin/security" element={<RequireAdmin><SecurityDashboard /></RequireAdmin>} />
            <Route 
              path="/admin/old" 
              element={
                <ProtectedRoute requireAuth={true} requireAdmin={true}>
                  <Admin />
                </ProtectedRoute>
              } 
            />
            
            {/* 404 Route */}
            <Route 
              path="*" 
              element={
                <div style={{ 
                  textAlign: 'center', 
                  padding: '100px 20px',
                  color: '#1E2A32'
                }}>
                  <h1>404 - Page Not Found</h1>
                  <p>The page you're looking for doesn't exist.</p>
                  <a href="/" style={{ color: '#5C9EAD' }}>
                    Go back home
                  </a>
                </div>
              } 
            />
          </Routes>
          </Suspense>
        </main>
        <MobileBar />
        <ChatbotWidget />
        <LocalBusinessJsonLD />
      </div>
      <Footer />

      {/* Toast notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#FFFFFF',
              color: '#1E2A32',
              border: '1px solid #E9ECEF',
            },
            success: {
              iconTheme: {
                primary: '#16A34A',
                secondary: '#FFFFFF',
              },
            },
            error: {
              iconTheme: {
                primary: '#DC2626',
                secondary: '#FFFFFF',
              },
            },
          }}
        />
    </Router>
  );
};

// App Component with Providers
const App = () => {
  useEffect(() => {
    // Initialize performance optimizations
    initPerformanceOptimizations();
    
    // Register Service Worker for PWA
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
            console.log('SW registered: ', registration);
          })
          .catch((registrationError) => {
            console.log('SW registration failed: ', registrationError);
          });
      });
    }
  }, []);
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <AuthProvider>
        <CartProvider>
          <AppContent />
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
